/**
 * offlineOutbox — a small, generic, durable outbox for field-capture writes that
 * must survive bad signal (offline queue + automatic flush on reconnect).
 *
 * This is the general mechanism the daily-log-specific `dailyLogOfflineDraft.ts`
 * anticipated ("building a general-purpose one is out of scope for THIS ticket") —
 * see docs/features/offline-capture-outbox.md. Slice 1 (#2442) wires the photo
 * capture flow through it; clock-in/out and other photo surfaces follow.
 *
 * Durability uses IndexedDB, not localStorage, because a queued op can carry a
 * photo *blob* that exceeds localStorage's ~5MB budget. The store is injectable
 * (setOutboxStore) so the enqueue / list / drain / dedup logic is unit-tested with
 * an in-memory adapter, no IndexedDB or device required.
 *
 * Safety contract: every op carries a client-generated `clientToken`. The server
 * dedups on it (project_photos.client_token), so a drain that replays an op whose
 * original request actually succeeded — but whose response was lost — does NOT
 * create a duplicate. Adding a retry queue is therefore safe: it cannot turn a
 * "lost photo" into a "duplicated photo".
 */

export interface OutboxOp {
  /** Unique queue-entry id (distinct from clientToken so re-enqueue is detectable). */
  id: string
  /** Flow discriminator, e.g. 'photo'. Drains are scoped per kind. */
  kind: string
  /** Idempotency key sent to the server so replays dedup. */
  clientToken: string
  /** When the user captured this, ISO-8601 — the event time, not the sync time. */
  capturedAt: string
  /** Small JSON metadata (projectId, fileName, …). No binary here. */
  payload: Record<string, unknown>
  /** Optional binary body (the photo bytes). */
  blob?: Blob
  /**
   * Optional multi-file binary body, for ops that must carry several files
   * atomically with one payload (e.g. team chat: 1-5 files sent together with
   * the message text) — a different shape than every other outbox consumer,
   * which attaches a single file to an already-existing entity. Additive:
   * every single-`blob` caller is unaffected (#2463).
   */
  blobs?: { blob: Blob; fileName: string }[]
  /** Flush attempts so far, for backoff / give-up bookkeeping. */
  attempts: number
}

/** Result a sender returns to drain(): remove on 'ok', keep-and-retry on 'retry'. */
export type SendResult = 'ok' | 'retry'
export type OutboxSender = (op: OutboxOp) => Promise<SendResult>

export interface OutboxStore {
  put(op: OutboxOp): Promise<void>
  list(kind?: string): Promise<OutboxOp[]>
  delete(id: string): Promise<void>
}

// ── IndexedDB store (default in the browser) ─────────────────────────────────

const DB_NAME = 'tenon-outbox'
const STORE = 'ops'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: 'id' })
        os.createIndex('kind', 'kind', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbReq<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

const indexedDBStore: OutboxStore = {
  async put(op) {
    const db = await openDB()
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).put(op)
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
      })
    } finally {
      db.close()
    }
  },
  async list(kind) {
    const db = await openDB()
    try {
      const tx = db.transaction(STORE, 'readonly')
      const all = await idbReq(tx.objectStore(STORE).getAll())
      const ops = (all as OutboxOp[]).filter(o => !kind || o.kind === kind)
      // FIFO: capturedAt is the stable capture order.
      ops.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
      return ops
    } finally {
      db.close()
    }
  },
  async delete(id) {
    const db = await openDB()
    try {
      const tx = db.transaction(STORE, 'readwrite')
      tx.objectStore(STORE).delete(id)
      await new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
        tx.onabort = () => reject(tx.error)
      })
    } finally {
      db.close()
    }
  },
}

let store: OutboxStore = indexedDBStore

/** Swap the backing store — used by tests to inject an in-memory adapter. */
export function setOutboxStore(s: OutboxStore): void {
  store = s
}

/** In-memory store, exported for tests (and a safe fallback if IndexedDB is absent). */
export function createMemoryStore(): OutboxStore {
  const map = new Map<string, OutboxOp>()
  return {
    async put(op) { map.set(op.id, op) },
    async list(kind) {
      const ops = [...map.values()].filter(o => !kind || o.kind === kind)
      ops.sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
      return ops
    },
    async delete(id) { map.delete(id) },
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

function newId(): string {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch { /* fall through */ }
  return `${Date.now()}-${Math.floor(Math.random() * 1e9).toString(36)}`
}

/** Idempotency key for a queued op's clientToken — same generation strategy as newId(). */
export function newClientToken(): string {
  return newId()
}

export interface EnqueueInput {
  kind: string
  clientToken: string
  capturedAt: string
  payload: Record<string, unknown>
  blob?: Blob
  blobs?: { blob: Blob; fileName: string }[]
}

/** Persist an op. Returns the stored op (with its generated id). */
export async function enqueue(input: EnqueueInput): Promise<OutboxOp> {
  const op: OutboxOp = {
    id: newId(),
    kind: input.kind,
    clientToken: input.clientToken,
    capturedAt: input.capturedAt,
    payload: input.payload,
    blob: input.blob,
    blobs: input.blobs,
    attempts: 0,
  }
  await store.put(op)
  return op
}

/** All pending ops of a kind, oldest first. */
export function listPending(kind?: string): Promise<OutboxOp[]> {
  return store.list(kind)
}

export function count(kind?: string): Promise<number> {
  return store.list(kind).then(ops => ops.length)
}

export function remove(id: string): Promise<void> {
  return store.delete(id)
}

/**
 * Flush every pending op of `kind` through `sender`, oldest first. On 'ok' the op
 * is removed; on 'retry' its attempt count is bumped and it is left for the next
 * drain. A sender that throws is treated as 'retry' (transient), never dropping
 * the op. Returns how many ops were successfully sent.
 *
 * Draining is sequential so a burst of queued captures replays in capture order
 * and never fans out concurrent uploads of the same bytes.
 */
// Single-flight per kind: two mounted drainers of the same kind (e.g. a page-
// level drainer plus a sheet's own usePhotoOutbox) must never replay the same
// op concurrently. A drain requested while one is running waits for it, then
// runs once more so ops enqueued in between are still sent.
const drainsInFlight = new Map<string, Promise<number>>()

export function drain(kind: string, sender: OutboxSender): Promise<number> {
  const prev = drainsInFlight.get(kind)
  const next = (prev ? prev.catch(() => 0) : Promise.resolve(0)).then(() => drainOnce(kind, sender))
  drainsInFlight.set(kind, next)
  next.finally(() => { if (drainsInFlight.get(kind) === next) drainsInFlight.delete(kind) }).catch(() => {})
  return next
}

async function drainOnce(kind: string, sender: OutboxSender): Promise<number> {
  const ops = await store.list(kind)
  let sent = 0
  for (const op of ops) {
    let result: SendResult
    try {
      result = await sender(op)
    } catch {
      result = 'retry'
    }
    if (result === 'ok') {
      await store.delete(op.id)
      sent++
    } else {
      await store.put({ ...op, attempts: op.attempts + 1 })
    }
  }
  return sent
}
